import { todayLocalDateParam } from '../../../utils/apiDate';

// The fields this helper reads; RecurringShiftRuleView and the drawer's untyped rules both fit.
export type GoverningRuleCandidate = {
  id: number;
  startDateUtc?: string | null;
  endDateUtc?: string | null;
  isArchived?: boolean | null;
};

const OPEN_END = '9999-12-31';

// The YYYY-MM-DD part of an API date. Compared as strings, never as Date objects: a date-only value
// parsed as UTC midnight is the previous local day west of UTC.
const datePart = (value?: string | null): string => (typeof value === 'string' ? value.slice(0, 10) : '');
const endDatePart = (value?: string | null): string => datePart(value) || OPEN_END;

/**
 * The rule that governs a schedule: the one its screens show and edit. A shift set can hold several
 * periods once the server splits a rule (moving the start date forward on a rule that already ran
 * caps the old rule and adds a new one), and the oldest rule is never the right answer: it is the
 * capped one, so editing it splits again and duplicates the pattern (Ibad, Oct 2026).
 *
 * Live rules are those whose end date is on or after today in the browser's calendar (no end =
 * open-ended). When some live rule starts after today, the one starting EARLIEST wins: the next
 * period to begin. Not "the rule covering today": right after a split the old capped rule still
 * covers today and the new one starts later. Not the latest start either: a middle split (new start
 * and a new end) also adds a tail with the OLD pattern after the new end, and that tail starts last.
 * Otherwise the live rule covering today with the latest start. Ties go to the higher id. With no
 * live rule, the rule that ended last. Null for an empty list.
 * Known limit: once a middle period is running, its old-pattern tail is the next period to begin and
 * wins; the dates alone cannot tell it from the Ibad shape.
 */
export function getGoverningRule<T extends GoverningRuleCandidate>(rules: readonly T[] | null | undefined): T | null {
  const candidates = (Array.isArray(rules) ? rules : []).filter((r) => r && !r.isArchived);
  if (candidates.length === 0) return null;
  const today = todayLocalDateParam();
  const live = candidates.filter((r) => endDatePart(r.endDateUtc) >= today);
  const upcoming = live.filter((r) => datePart(r.startDateUtc) > today);
  if (upcoming.length > 0) return pick(upcoming, (r) => datePart(r.startDateUtc), 'earliest');
  if (live.length > 0) return pick(live, (r) => datePart(r.startDateUtc), 'latest');
  return pick(candidates, (r) => endDatePart(r.endDateUtc), 'latest');
}

function pick<T extends GoverningRuleCandidate>(pool: readonly T[], key: (r: T) => string, order: 'earliest' | 'latest'): T {
  return pool.reduce((best, r) => {
    const a = key(r);
    const b = key(best);
    if (a !== b) return (order === 'latest' ? a > b : a < b) ? r : best;
    return r.id > best.id ? r : best;
  });
}
